import { RiCloseCircleFill } from '@remixicon/react';

import {
  Button,
  Flex,
  Icon,
  Text,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import { AiLoadingIndicator } from '@spotify/confidence-core-react';
import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';

import {
  EXPERIMENTATION_LOADER_PHRASES,
  MANAGED_WAREHOUSE_LOADER_PHRASES,
} from './experimentationLoaderPhrases';
import { getSignupErrorMessage } from './signupUtils';

import styles from './ExperimentationLoader.module.css';

type ExperimentationLoaderProps = {
  error?: Error;
  onResultDismissed: () => void;
};

export const ExperimentationLoader = ({
  error,
  onResultDismissed,
}: ExperimentationLoaderProps) => {
  const { enabled: isManagedWarehouse } = useFeatureFlag<{
    enabled: boolean;
  }>('managed-warehouse', { enabled: false });
  const phrases = isManagedWarehouse
    ? MANAGED_WAREHOUSE_LOADER_PHRASES
    : EXPERIMENTATION_LOADER_PHRASES;

  if (!error) {
    return (
      <>
        <VisuallyHidden role="status">Creating workspace</VisuallyHidden>
        <div aria-hidden>
          <AiLoadingIndicator
            phrases={phrases}
            spinnerSize="large"
            textSize="medium"
            alignment="center"
          />
        </div>
      </>
    );
  }

  return (
    <Flex
      className={styles.result}
      direction="column"
      alignItems="center"
      gap="looser-2"
      role="alert"
    >
      <Icon
        as={RiCloseCircleFill}
        size="large"
        semanticColor="textNegative"
        aria-hidden
      />
      <Text variant="bodyMedium" semanticColor="textSubdued">
        {getSignupErrorMessage(error)}
      </Text>
      <Button
        variant="secondary-bordered"
        size="medium"
        onClick={onResultDismissed}
      >
        Back to workspace setup
      </Button>
    </Flex>
  );
};
